import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { Footer } from 'common/components/Footer';
import { Header } from 'common/components/Header';
import { Seo } from 'common/components/SEO';
import { ToastMessage } from 'common/components/ToastMessage';
import { IAnyType } from 'common/types/common';
import { domAnimation, LazyMotion, m } from 'framer-motion';
import { gsap } from 'gsap';
import dynamic from 'next/dynamic';

import { anim, expand, opacity } from './anim';

const Cookies = dynamic(() => import('common/components/CookieConsent').then(mod => mod.Cookies), {
  ssr: false,
});
const CursorFollower = dynamic(
  () => import('common/components/CursorFollower').then(mod => mod.default),
  { ssr: false },
);

interface TooltipContextValue {
  tooltip: string;
  handleSetTooltip: (value: string) => void;
}

export const TooltipContext = createContext<TooltipContextValue>({
  tooltip: '',
  handleSetTooltip: () => undefined,
});

const NUMBER_OF_COLUMNS = 8;

export const Layout = ({ children, className, dark, seo, footerBorderTop }: IAnyType) => {
  const el = useRef(null);
  const q = gsap.utils.selector(el);

  const [tooltip, setTooltip] = useState('');

  const handleSetTooltip = (value: string) => {
    setTooltip(value);
  };

  const onScroll = useCallback(() => {
    if (tooltip) {
      setTooltip('');
    }
  }, [tooltip]);

  useEffect(() => {
    gsap.timeline({ delay: 1 }).to(q('header'), { duration: 0.6, top: 0 });
  }, [q]);

  useEffect(() => {
    document.addEventListener('scroll', onScroll, { passive: true });
    return () => document.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  return (
    <>
      <Seo {...seo} />
      <LazyMotion features={domAnimation}>
        <div className="page stairs">
          <m.div {...anim(opacity)} className="transition-background" />
          <div className="transition-container">
            {[...Array(NUMBER_OF_COLUMNS)].map((_, i) => {
              return <m.div key={i} {...anim(expand, NUMBER_OF_COLUMNS - i)} />;
            })}
          </div>

          <ToastMessage>
            <TooltipContext.Provider value={{ handleSetTooltip, tooltip }}>
              <div ref={el} className={className}>
                <Header alternate={seo.alternate} dark={dark} languageSwitch={seo.languageSwitch} />
                <main>{children}</main>

                <Footer borderTop={footerBorderTop} />

                <CursorFollower tooltip={tooltip} />
              </div>
            </TooltipContext.Provider>
          </ToastMessage>
        </div>
      </LazyMotion>

      <Cookies />
    </>
  );
};
